/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import VmForm from '@web/src/features/vendor-market/vm-form/vm-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreateVendorMarketInput,
  CreateVendorMarketMutation,
  CreateVendorMarketMutationVariables,
} from 'types/graphql'

const CREATE_SP_MUTATION: TypedDocumentNode<CreateVendorMarketMutation, CreateVendorMarketMutationVariables> = gql`
  mutation CreateVendorMarket($input: CreateVendorMarketInput!) {
    createVendorMarket(input: $input) {
      id
      slug
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/create')({
  component: CreateVendorMarketPage,
  head: () =>
    seo({
      title: `Create Vendor | ${env.VITE_APP_NAME}`,
      description: 'Create a new vendor listing',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function CreateVendorMarketPage() {
  const { t } = useTranslation()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()

  const [createSP] = useMutation(CREATE_SP_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Vendor Created'))
      navigate({
        to: '/vendor-market/$vendorMarketIdOrSlug',
        params: {
          vendorMarketIdOrSlug: result.createVendorMarket.slug ?? result.createVendorMarket.id,
        },
      } as never)
      setShowSpinnerValue(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreateVendorMarketInput) => {
    setShowSpinnerValue(true)
    createSP({ variables: { input } })
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Create Vendor')}</h1>
        <VmForm onSave={onSave} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
